
import { SBoolean, SNumber, SString } from '@/common/model';
import { format, startOfWeek, endOfWeek, addWeeks } from 'date-fns';

export const randomID = (length: number = 32) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  
  return result;
}

/**
 * Converts a string to a color.
 * 
 * @param str - The input string.
 * @returns A hex color string.
 */
export const hexFromStr = (str:string) => {
  let hash = 0;
  // Compute a hash from the input string
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Convert the hash to a hex color code
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
  }
  
  return color;
}

/**
 * Converts a hex color string to an RGB object.
 * 
 * @param {string} hex - The hex color string.
 * @returns {Object} An object with r, g, b properties.
 */
const hexToRgb = (hex) => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '');
  
  // Parse the r, g, b values
  let bigint = parseInt(hex, 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;

  return { r, g, b };
};

/**
* Calculates the luminance of an RGB color.
* 
* @param {number} r - Red component (0-255).
* @param {number} g - Green component (0-255).
* @param {number} b - Blue component (0-255).
* @returns {number} The luminance of the color.
*/
const getLuminance = (r, g, b) => {
  // Convert RGB to luminance
  let a = [r, g, b].map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });

  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

/**
* Determines the appropriate text color (black or white) for a given background color.
* 
* @param {string} backgroundColor - The background color in hex format.
* @returns {string} The text color in hex format (#000000 or #FFFFFF).
*/
const getTextColorForBackground = (backgroundColor) => {
  const { r, g, b } = hexToRgb(backgroundColor);
  const luminance = getLuminance(r, g, b);
  
  // If the luminance is higher than 0.5, the background is light, so return black. Otherwise, return white.
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/**
 * Inverts a hex color.
 * 
 * @param hex - The hex color string.
 * @returns The inverted hex color string.
 */
export const invertColor = (hex:string) => {
     return getTextColorForBackground(hex);
}

/**
 * Converts a number from 1 to 20 to a color ranging from green to yellow to red.
 * 
 * @param num - The input number (1 to 20).
 * @returns A hex color string.
 */
export const numberToColor = (num: number) => {
    if (num < 1) {
       num = 1;
    }

    if(num > 20){
        num = 20;
    }

    // Normalize num to a range of 0 to 1
    const normalized = (num - 1) / 19;

    // Calculate the color
    let r, g;
    if (normalized <= 0.5) {
        // Green to Yellow
        r = Math.floor(255 * (normalized * 2));
        g = 255;
    } else {
        // Yellow to Red
        r = 255;
        g = Math.floor(255 * ((1 - normalized) * 2));
    }
    const b = 0;

    // Convert to hex string
    const hex = (r << 16) | (g << 8) | b;
    return `#${hex.toString(16).padStart(6, '0')}`;
}



export const getThisAndNextWeekDesc = () => {
  const getWeekRange = (date) => {
    const start = startOfWeek(date, { weekStartsOn: 1 });
    const end = endOfWeek(date, { weekStartsOn: 1 });
    return `${format(start, 'MM月dd日')}-${format(end, 'MM月dd日')}`;
  };

  const today = new Date();
  const thisWeekRange = getWeekRange(today);
  const nextWeekRange = getWeekRange(addWeeks(today, 1));

  return {
    currentWeek: thisWeekRange,
    nextWeek: nextWeekRange
  };
}; 

export const serializeMap = (map) => {
  return JSON.stringify(Array.from(map.entries()));
};

export const deserializeMap = (string) => {
  return new Map(JSON.parse(string));
};



export const  formatTimeDifference = (inputTime) => {
  const now = new Date();
  const inputDate = new Date(inputTime);
  const diffInSeconds = Math.floor((now.getTime() - inputDate.getTime()) / 1000);

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 3) {
    return `刚刚`;
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}分钟前`;
  } else if (diffInHours < 24) {
    return `${diffInHours}小时前`;
  } else if (diffInDays < 7) {
    return `${diffInDays}天前`;
  } else {
    const year = inputDate.getFullYear();
    const month = String(inputDate.getMonth() + 1).padStart(2, '0'); // 月份从0开始，需要+1
    const day = String(inputDate.getDate()).padStart(2, '0');
    const hours = String(inputDate.getHours()).padStart(2, '0');
    const minutes = String(inputDate.getMinutes()).padStart(2, '0');
    const seconds = String(inputDate.getSeconds()).padStart(2, '0');
    return `${month}月${day}日`;
  } 
}


/**
 * 返回状态对应的颜色，从灰色到绿色渐变
 * @param {number} currentStep 当前状态的顺序，从 1 开始
 * @param {number} totalSteps 状态的总数
 * @returns {string} 颜色的十六进制字符串
 */
export const getStatusColor = (currentStep, totalSteps) => {
  const interpolate = (start, end, step, total) => {
    return Math.round(start + ((end - start) * (step - 1) / (total - 1)));
  };

  // 灰色 (128, 128, 128) 到 绿色 (0, 128, 0)
  const startColor = { r: 128, g: 128, b: 128 };
  const endColor = { r: 0, g: 128, b: 0 };

  const r = interpolate(startColor.r, endColor.r, currentStep, totalSteps);
  const g = interpolate(startColor.g, endColor.g, currentStep, totalSteps);
  const b = interpolate(startColor.b, endColor.b, currentStep, totalSteps);

  // 将 RGB 转换为十六进制
  const toHex = (value) => {
    const hex = value.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
 

export const getStatusStyle = (name:string)=>{

  if(!name){
    return 'default';
  }
  if(name.indexOf('未')>=0){
    return 'red';
  }
  if(name.indexOf('中') >=0 || name.indexOf('进行')>=0){
    return 'processing';
  }

  if(name.indexOf('已') >=0 || name.indexOf('已经') >=0 || name.indexOf('完成')>=0 ){
    return 'success';
  }

  if(name.indexOf('延期') >=0 || name.indexOf('过期') >=0){
    return 'error';
  }
  
  if(name.indexOf('待') >=0){
    return 'default';
  }

  return 'default';

}

/** 交付项状态 Tag 文字色（含「待」为深灰，配合 default 灰底） */
export const getStatusTagTextStyle = (name: string): { color?: string } => {
  if (name && name.indexOf('待') >= 0) {
    return { color: '#595959' };
  }
  return {};
};

